import { useState } from "react";
import { CmsEmptyState } from "@/components/ResearchCms";
import { filterTestimonials, TESTIMONIAL_FILTERS } from "../../utils/filterTestimonials";
import type { TestimonialFilter } from "../../utils/filterTestimonials";
import type { SourceDataFirm } from "../../utils/sourceDataFirm";
import { testimonialCards } from "../../utils/testimonialCards";
import type { TestimonialPageNames } from "../../utils/testimonialCards";
import styles from "./TestimonialsTab.module.css";
import { FilterChips } from "./FilterChips";
import { ErrorState, StatusBadge, UnknownState } from "./SourceDataStates";
import { useTestimonials } from "./useSourceData";

type TestimonialsTabProps = {
  firm: SourceDataFirm;
  editionId: string;
  researchBpaId: string;
  names: TestimonialPageNames;
  /** Requests only while true (default true) — the panel passes its open state. */
  enabled?: boolean;
};

const NONE_FOUND = "No testimonials found";

export function TestimonialsTab({
  firm,
  editionId,
  researchBpaId,
  names,
  enabled = true,
}: TestimonialsTabProps) {
  const outcome = useTestimonials({ firm, editionId, researchBpaId, enabled });
  // The filter belongs to the firm, edition and BPA it was chosen for; a different one starts at Approved.
  const scope = [
    firm.kind === "known" ? firm.companyUuid : firm.kind,
    editionId,
    researchBpaId,
  ].join("|");
  const [chosen, setChosen] = useState<{ scope: string; filter: TestimonialFilter }>({
    scope,
    filter: "approved",
  });
  const filter = chosen.scope === scope ? chosen.filter : "approved";

  if (outcome.kind === "loading") {
    return <CmsEmptyState loading message="Loading testimonials" />;
  }
  if (outcome.kind === "error") {
    return <ErrorState message="Testimonials could not be loaded. Try again shortly." />;
  }
  if (outcome.kind === "unknown") {
    return (
      <UnknownState
        title="Testimonials can't be looked up for this entry"
        sentence={outcome.sentence}
      />
    );
  }
  if (outcome.kind === "empty") return <CmsEmptyState message={NONE_FOUND} />;

  const cards = testimonialCards(filterTestimonials(outcome.data, filter), names);

  return (
    <div className={styles.tab}>
      <FilterChips
        label="Filter testimonials"
        options={TESTIMONIAL_FILTERS}
        selected={filter}
        onSelect={id => setChosen({ scope, filter: id })}
      />
      {cards.length === 0 && <CmsEmptyState message={NONE_FOUND} />}
      {cards.map(card => (
        <article key={card.id} className={styles.card}>
          <header className={styles.cardHead}>
            <h3 className={styles.publication}>{card.publication}</h3>
            {card.status && <StatusBadge label={card.status} testId="approval-status" />}
          </header>
          <blockquote className={styles.quote}>
            <p>{card.quote}</p>
          </blockquote>
          <dl className={styles.rows}>
            {card.rows.map(row => (
              <div key={row.label} className={styles.row}>
                <dt className={styles.label} title={row.tooltip ?? undefined}>
                  {row.label}
                </dt>
                <dd className={styles.value}>{row.value}</dd>
              </div>
            ))}
          </dl>
        </article>
      ))}
    </div>
  );
}
